import { Injectable } from '@angular/core';
import { CurrencyPipe } from '@angular/common';

import { reportStyles } from './reports/report-styles';


@Injectable({
  providedIn: 'root',
})
export class RoyaltyReportDataService {

  constructor(private $currency: CurrencyPipe) { }

  calcTotals({ data, settings }): any {
    const orders = flattenOrders({ data });
    const exemptions = settings.isFranchise ? flattenExemptions({ orders }) : [];

    // calc order totals
    orders.forEach(order => { order.amount = order.products.reduce((sum, p) => sum + p.amount || 0, 0); });

    // calc totals
    const totals: Record<string, any> = {
      totalRevenue: orders.reduce((sum, o) => sum + o.amount, 0),
      exemptRevenue: exemptions.reduce((sum, e) => sum + e.amount, 0),
    };
    totals.royaltiesRevenue = totals.totalRevenue - totals.exemptRevenue;
    totals.fees = settings.franchiseFees.map(fee => ({ ...fee, amount: fee.type === '$' ? fee.rate: (fee.rate * totals.royaltiesRevenue) / 100 }));
    totals.totalDue = totals.fees.reduce((sum, f) => sum + f.amount, 0);

    // round totals
    totals.fees.forEach(fee => { fee.amount = round(fee.amount); });
    Object.keys(totals).forEach(key => {
      if (key !== 'fees') {
        totals[key] = round(totals[key]);
      }
    })

    return totals;
  }

  dataToReport({ grandTotals, sites, sitesMeta }) {
    const result = {
      columns: [
        { dataKey: 'center', title: 'Center' },
        { dataKey: 'totalRevenue', title: 'Total Revenue', styles: { halign: 'right' } },
        { dataKey: 'exemptRevenue', title: 'Exempt Revenue', styles: { halign: 'right' } },
        { dataKey: 'royaltiesRevenue', title: 'Revenue Eligible for Royalties', styles: { halign: 'right' } },
      ],
      rows: [],
      styles: [],
    };

    if (grandTotals.fees && grandTotals.fees.length) {
      result.columns = result.columns.concat(grandTotals.fees.map(fee => ({ title: fee.name, styles: { halign: 'right' } })));
      result.columns.push({ dataKey: 'totalDue', title: 'Total Due', styles: { halign: 'right' } });
    }

    let row: Record<string, any>[] = [];

    result.styles.push(reportStyles.h2);
    result.rows.push([{ content: 'US Based', colSpan: result.columns.length, styles: { halign: 'left' } }]);

    result.styles.push(reportStyles.h3);

    const $curr = (val, code?) => this.$currency.transform(val, code, 'symbol-narrow');

    row = [
      { content: 'TOTALS', styles: { halign: 'left' } },
      { content: $curr(grandTotals.totalRevenue), styles: { halign: 'right' } },
      { content: $curr(grandTotals.exemptRevenue), styles: { halign: 'right' } },
      { content: $curr(grandTotals.royaltiesRevenue), styles: { halign: 'right' } },
    ];

    if (grandTotals.fees && grandTotals.fees.length) {
      row = row.concat(grandTotals.fees.map(fee => ({ content: $curr(fee.amount), styles: { halign: 'right' } })));
      row.push({ content: $curr(grandTotals.totalDue), styles: { halign: 'right' } });
    }

    result.rows.push(row);

    const centers: any[] = [];
    for (const site of sites) {
      if (sitesMeta[site._id].centers) {
        for (const center of sitesMeta[site._id].centers) {
          if (center.totals) {
            center.currency = sitesMeta[site._id].currency;
            centers.push(center);
          }
        }
      }
    }

    centers.filter(c => c.currency === 'USD').forEach((center, idx) => pushCenterRow(center, idx));

    result.styles.push(reportStyles.h2);
    result.rows.push([{ content: 'International', colSpan: result.columns.length, styles: { halign: 'left' } }]);

    centers.filter(c => c.currency !== 'USD').forEach((center, idx) => pushCenterRow(center, idx));

    return result;

    function pushCenterRow(center, idx) {
      row = [
        { content: center.name, styles: { halign: 'left' } },
        { content: $curr(center.totals.totalRevenue, center.currency), styles: { halign: 'right' } },
        { content: $curr(center.totals.exemptRevenue, center.currency), styles: { halign: 'right' } },
        { content: $curr(center.totals.royaltiesRevenue, center.currency), styles: { halign: 'right' } },
      ];

      if (center.totals.fees && center.totals.fees.length) {
        row = row.concat(center.totals.fees.map(fee => ({ content: $curr(fee.amount, center.currency), styles: { halign: 'right' } })));
        row.push({ content: $curr(center.totals.totalDue, center.currency), styles: { halign: 'right' } });
      }

      result.rows.push(row);
      result.styles.push(idx % 2 ? null : reportStyles.rowOdd);
    }
  }

}

function flattenOrders({ data }) {
  const orders = [];

  data.forEach(productType => {
    productType.productCategories.forEach(category => {
      category.products.forEach(product => {
        product.amount = +product.amount;
        let order = orders.find(o => o.orderNo === product.orderNo);
        if (!order) {
          order = { orderNo: product.orderNo, products: [] };
          orders.push(order);
        }
        order.products.push(product);
      });
    });
  });

  return orders;
}

function flattenExemptions({ orders }) {
  return orders.reduce((arr, order) => arr.concat(order.products.filter(p => p.royaltyExempt)), []);
}

function round(val, fractions = 2) {
  if (isNaN(+val)) return val;
  const factor = +('1000000'.slice(0, fractions + 1));
  return Math.round(val * factor) / factor;
}
